import styles from "./Suggestion.module.scss"

import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

import { KEY_CODE } from "@shared/constants"
import { trimAll } from "@shared/modules"

import classNames from "classnames"

const START_INDEX = 0

interface Props {
  inputRef: RefObject<HTMLInputElement>
  searchQuery: string
  sentViewer: string[]
  handleIsTrigger: Function
  handleMessage: Function
}

export default function Suggestion({
  inputRef,
  searchQuery,
  sentViewer,
  handleIsTrigger,
  handleMessage,
}: Props) {
  const [focusIndex, setFocusIndex] = useState<number>(0)
  const searchQueryRef = useRef("")
  const suggestionCountRef = useRef(0)

  useEffect(() => {
    suggestionCountRef.current = sentViewer.length
  }, [sentViewer])

  useEffect(() => {
    searchQueryRef.current = searchQuery
    setFocusIndex(0)
  }, [searchQuery])

  const shiftFocus = useCallback((shiftNum: number) => {
    setFocusIndex(
      (prevIndex) =>
        (suggestionCountRef.current + prevIndex + shiftNum) %
        suggestionCountRef.current,
    )
  }, [])

  const replaceMention = useCallback(
    (clickIndex?: number) => {
      const selectedIndex = clickIndex === undefined ? focusIndex : clickIndex

      if (!inputRef.current || !inputRef.current.selectionEnd) return

      const { value, selectionEnd } = inputRef.current

      !sentViewer[selectedIndex]
        ? handleMessage(
            `${value.substr(START_INDEX, selectionEnd)} ${value.substr(
              selectionEnd,
              value.length,
            )}`,
          )
        : handleMessage(
            `${value.substr(
              START_INDEX,
              selectionEnd - searchQueryRef.current.length,
            )}${trimAll(sentViewer[selectedIndex])} ${value.substr(
              selectionEnd,
              value.length,
            )}`,
          )

      handleIsTrigger(false)
    },
    [inputRef, focusIndex, handleIsTrigger, handleMessage, sentViewer],
  )

  const removeKeyEvent = useCallback((e: KeyboardEvent) => {
    e.cancelBubble = true
    e.returnValue = false
    e.preventDefault()
    e.stopPropagation()
  }, [])

  useEffect(() => {
    const keyPressEvent = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        removeKeyEvent(e)
        replaceMention()
      }
      return
    }
    const keyDownEvent = (e: KeyboardEvent) => {
      if (Object.values(KEY_CODE.Arrow).includes(e.keyCode))
        switch (e.keyCode) {
          case KEY_CODE.Arrow.UP:
            removeKeyEvent(e)
            shiftFocus(-1)
            return
          case KEY_CODE.Arrow.DOWN:
            removeKeyEvent(e)
            shiftFocus(+1)
            return
        }
    }

    window.addEventListener("keypress", keyPressEvent)
    window.addEventListener("keydown", keyDownEvent)

    return () => {
      window.removeEventListener("keypress", keyPressEvent)
      window.removeEventListener("keydown", keyDownEvent)
    }
  }, [replaceMention, shiftFocus, removeKeyEvent])

  return (
    <div className={styles.suggestion}>
      <ul>
        {!sentViewer.length ? (
          <li>일치하는 항목이 없습니다.</li>
        ) : (
          sentViewer.map((viewer, index) => {
            return (
              <li
                className={classNames({
                  [styles.focus]: index === focusIndex,
                })}
                key={viewer}
                onClick={() => {
                  replaceMention(index)
                  inputRef.current?.focus()
                }}
              >
                {viewer}
              </li>
            )
          })
        )}
      </ul>
    </div>
  )
}
